import { Component, ErrorInfo, ReactNode } from "react";
import { isChunkLoadError, reloadOnceForChunkError } from "../../utils/chunkReload";
import "../../Css/Error/404.css";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * App-wide safety net. Without this, any error thrown during render (including a
 * rejected lazy-route dynamic import after a deploy) unmounts the whole React tree
 * and leaves a blank white screen.
 *
 * For stale-chunk errors it recovers invisibly by reloading onto the freshly
 * deployed build. For anything else it shows a friendly fallback instead of a
 * blank page.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: unknown): State {
    // A stale-chunk failure is recoverable: reload onto the new build instead of
    // showing the fallback. reloadOnceForChunkError is guarded against loops, so
    // if it declines to reload we fall through and render the fallback.
    if (isChunkLoadError(error) && reloadOnceForChunkError()) {
      return { hasError: false };
    }
    return { hasError: true };
  }

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    // console.error is forwarded to Logfire by TelemetryService.
    console.error("Uncaught error in React tree:", error, info?.componentStack);
  }

  componentDidUpdate(_prevProps: Props, prevState: State): void {
    if (this.state.hasError && !prevState.hasError) {
      let robots = document.querySelector('meta[name="robots"]');
      if (!robots) {
        robots = document.createElement("meta");
        robots.setAttribute("name", "robots");
        document.head.appendChild(robots);
      }
      robots.setAttribute("content", "noindex, nofollow");
    }
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="error_container">
        <div className="error_contentColumn">
          <i className="fa-sharp-duotone fa-solid fa-face-sad-sweat"></i>
          <div className="error_title">Something went wrong</div>
          <div className="error_description">
            An unexpected error occurred. Reloading usually fixes it.
          </div>
          <div className="error_footerButton" onClick={this.handleReload}>
            <i className="fa-duotone fa-solid fa-rotate-right"></i>
            <div className="error_footerButtonText">Reload</div>
          </div>
        </div>
      </div>
    );
  }
}
